import { RefObject, useCallback } from 'react';
import useArrowKeyCycleFocus from '@/utils/use-arrow-key-cycle-focus';
import { HandleOpenProps } from '../popover.types';

export type UsePopoverKeyboardControlsParams = {
  cycleWithArrowKeys?: boolean;
  closeOnEnter: boolean;
  openWithArrowKeys: boolean;
  open: boolean;
  display: string;
  portalEl?: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  handleOpen: (meta?: HandleOpenProps) => void;
  handleClose: () => void;
};

export type UsePopoverKeyboardControlsReturn = {
  handleOpenWithArrowKeys: (event: React.KeyboardEvent) => void;
};

/**
 * Consolidate keyboard interaction logic including arrow key opening and cycling.
 */
export function usePopoverKeyboardControls({
  cycleWithArrowKeys,
  closeOnEnter,
  openWithArrowKeys,
  open,
  display,
  portalEl,
  contentRef,
  handleOpen,
  handleClose,
}: UsePopoverKeyboardControlsParams): UsePopoverKeyboardControlsReturn {
  useArrowKeyCycleFocus({
    containerRef: cycleWithArrowKeys && open && display !== 'none' ? portalEl || contentRef : null,
    onTab: () => handleClose(),
    onEnter: () => closeOnEnter && handleClose(),
  });

  const handleOpenWithArrowKeys = useCallback(
    (event: React.KeyboardEvent) => {
      if (openWithArrowKeys && !open && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        event.preventDefault();
        handleOpen({ keyDirection: event.key });
      }
    },
    [handleOpen, open, openWithArrowKeys]
  );

  return {
    handleOpenWithArrowKeys,
  };
}
